module Meevo.Components.Controls {
    "use strict";
    interface IPaymentAccountScope extends angular.IScope {
        model: Model.PaymentAccount;
        canDelete: boolean;
        delete(): void;
        merchantTerminalId: string;
        merchantAccountId: string;
        merchantProviderId: number;
        drawerId: string;
        enableDualLockBehavior: boolean;
        merchantTerminalLoading: boolean;
        merchantAccount: any;
        merchantTerminal: any;
        processCreditCard(): void;
        isCredit: boolean;
        isCreditChanged(): void;
        deletePaymentAccount(event: ng.IAngularEvent);
        useHostedPage(): boolean;
        disabled: boolean;
        directManualEntry: boolean;
        autoDeletePreviousPaymentAccount: boolean;
        showAchInfo(number: string, numberType: string): void;
        hidePaymentAccountTypeSelector: boolean;
        showHsaFsa: boolean;
        showHsaFsaAfterAccountCreation: boolean;
        hostedPageIntent: Meevo.Model.HostedPageIntent;
        canEditExpirationDate: boolean;
        cardholderName: string;
        states: Array<{ state: string }>;
        zipCodeRegEx: RegExp;
        cardholderNameRegEx: RegExp;
        showFullBillingAddress(): boolean;
        isMeevoPayProvider(): boolean;
        onCountryChange(newCountry: string): void;
    }
    export class PaymentAccount implements angular.IDirective {
        constructor(
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            private busyService: Meevo.Components.Busy.IBusyService,
            private modalService: Meevo.Components.Modal.IModalService,
            private addressFieldsService: Meevo.Components.Common.IAddressFieldsService,
            private tippyService: Meevo.Plugins.Tippy.TippyService
        ) {
        }

        restrict = "E";
        require = "ngModel";
        templateUrl = "app/components/controls/paymentAccounts/paymentAccount.template.html";
        scope = {
            model: "=ngModel",
            canDelete: "=",
            delete: "&",
            merchantTerminalId: "=",
            merchantAccountId: "=",
            merchantProviderId: "=",
            drawerId: "=?",
            enableDualLockBehavior: "=?",
            disabled: "=?ngDisabled",
            directManualEntry: "=?",
            autoDeletePreviousPaymentAccount: "=",
            elevationChallenge: '=?',
            hidePaymentAccountTypeSelector: "=",
            showHsaFsa: '=?',
            showHsaFsaAfterAccountCreation: '=?',
            hostedPageIntent: '=?',
            canEditExpirationDate: '=?',
            isHostedPageAutomaticallySwitch: '=?',
            disableBillingAddressEdit: '=?',
            cardholderName: '=?'
        };

        isViewACHElevated: boolean = false;

        updateModel(scope: IPaymentAccountScope, ngModel, response) {
            scope.model.creditCardLastFour = response.creditCardLastFour;
            scope.model.creditCardExpirationDate = response.creditCardExpirationDate;
            scope.model.externalPaymentAccountId = response.externalPaymentAccountId;
            scope.model.merchantProviderId = scope.merchantProviderId;
            scope.model.id = response.id;
            ngModel.$validate();
        }

        private togglePaymentType(status: boolean):  Model.PaymentAccountTypeEnum {
            return status ? Model.PaymentAccountTypeEnum.Card : Model.PaymentAccountTypeEnum.ACHChecking;
        }

        link = (scope: IPaymentAccountScope, element: JQuery, attr: angular.IAttributes, ngModel) => {
            scope.isCredit = scope.model.paymentAccountType == Model.PaymentAccountTypeEnum.Card;
            scope.states = [];
            scope.cardholderNameRegEx = /^[-a-zA-Z.`'~ ]*$/;
            let previousCountry: string;

            scope.isMeevoPayProvider = (): boolean => {
                return !!scope.model && Meevo.Model.MerchantAccountProvider.isMeevoPayProvider(scope.model.merchantProviderId);
            };

            scope.showFullBillingAddress = (): boolean => {
                return !scope.model.disableBillingAddressEdit
                    && !!scope.model.externalPaymentAccountId
                    && scope.isMeevoPayProvider();
            };

            const initAddressForEdit = (): void => {
                if (!scope.showFullBillingAddress()) {
                    return;
                }
                if (!scope.model.country) {
                    scope.model.country = this.addressFieldsService.getDefaultCountryForProvider(scope.model.merchantProviderId);
                }
                previousCountry = scope.model.country;
                scope.zipCodeRegEx = this.addressFieldsService.getZipCodeRegEx(scope.model.country);
                scope.states = [];
                this.addressFieldsService.getStates(scope.model.country).then(list => {
                    scope.states = list || [];
                });
            };

            scope.onCountryChange = (newCountry: string): void => {
                scope.states = [];
                this.addressFieldsService
                    .handleCountryChange(scope.model, newCountry, previousCountry)
                    .then(list => {
                        scope.states = list || [];
                        previousCountry = scope.model.country;
                        scope.zipCodeRegEx = this.addressFieldsService.getZipCodeRegEx(scope.model.country);
                    });
            };

            initAddressForEdit();
            scope.$watch('model.externalPaymentAccountId', (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    initAddressForEdit();
                }
            });

            this.dataService.customMethodData(`api/merchantAccount/${scope.merchantAccountId}`, "GET", null).then((response: any) => {
                scope.merchantAccount = response.data;
                scope.hasAchSupport = response.data.hasAchSupport;
            });

            let merchantTerminalRequest = {
                merchantAccountId: scope.merchantAccountId,
            };

            scope.$watch("merchantTerminalId", () => {
                if (scope.merchantTerminalId && (!scope.merchantTerminal || scope.merchantTerminal.id != scope.merchantTerminalId)) {
                    // Disables Initiate Card Reader/Add New while the terminal (and its TippyKioskId) is
                    // still loading, so the kiosk-lock check can't be clicked past before it has the
                    // terminal it needs to check against.
                    scope.merchantTerminalLoading = true;
                    this.dataService.customMethodData(`api/merchantTerminal/${scope.merchantTerminalId}`, "POST", merchantTerminalRequest)
                        .then((response: any) => {
                            scope.merchantTerminal = response.data;
                        })
                        .finally(() => {
                            scope.merchantTerminalLoading = false;
                        });
                }
            });

            if (Model.Guard.isNothing(scope.autoDeletePreviousPaymentAccount)) {
                scope.autoDeletePreviousPaymentAccount = false;
            }

            // need to allow invalid so that model isn't set to undefined by angular validator if validation fails
            ngModel.$options = ngModel.$options || {};
            ngModel.$options.allowInvalid = true;
            ngModel.$validators.required = (modelValue, viewValue) => {
                return !scope.isCredit || modelValue.externalPaymentAccountId != undefined;
            };

            const openAddCreditPaymentAccountModal = () => {
                const request = {
                    templateUrl: "app/components/controls/paymentAccounts/addCreditPaymentAccount.template.html",
                    controller: "meevo.components.controls.AddCreditPaymentAccountController",
                    controllerAs: "vm",
                    bindToController: true,
                    locals: {
                        zipCode: scope.model.zipCode,
                        useHostedPage: scope.useHostedPage(),
                        paymentAccountId: scope.model.id,
                        merchantAccountId: scope.merchantAccountId,
                        merchantProviderId: scope.merchantProviderId,
                        merchantTerminalId: scope.merchantTerminalId,
                        merchantTerminal: scope.merchantTerminal,
                        drawerId: scope.drawerId,
                        enableDualLockBehavior: scope.enableDualLockBehavior,
                        clientId: undefined,
                        isHsaFsa: scope.model.isHsaFsa,
                        showHsaFsa: scope.showHsaFsa,
                        showHsaFsaAfterAccountCreation: scope.showHsaFsaAfterAccountCreation,
                        hostedPageIntent: scope.hostedPageIntent,
                        paymentAccountType: scope.model.paymentAccountType,
                        forceManualEntry: scope.model.forceManualEntry,
                        isHostedPageAutomaticallySwitch: scope.isHostedPageAutomaticallySwitch,
                        disableBillingAddressEdit: scope.disableBillingAddressEdit,
                        cardholderName: scope.cardholderName,
                    }
                };

                this.modalService.showCustom(request)
                    .then(response => {
                        scope.model.zipCode = response.zipCode;
                        scope.model.isHsaFsa = response.isHsaFsa;
                        scope.model.disableBillingAddressEdit = response.disableBillingAddressEdit;
                        if (response.isFullBillingAddress && response.paymentResponse) {
                            scope.model.address1 = response.paymentResponse.billingAddress1;
                            scope.model.address2 = response.paymentResponse.billingAddress2;
                            scope.model.city = response.paymentResponse.billingCity;
                            scope.model.state = response.paymentResponse.billingState;
                            scope.model.zipCode = response.paymentResponse.billingPostalCode;
                            scope.model.country = response.paymentResponse.billingCountryCode;
                            scope.model.cardholderName = response.paymentResponse.cardholderName;
                        }

                        if (response.hostedPageTerminal) {
                            scope.merchantTerminal = response.hostedPageTerminal;
                            scope.merchantTerminalId = response.hostedPageTerminal.id;
                        }

                        if (!scope.model.id || !scope.autoDeletePreviousPaymentAccount) {
                            this.updateModel(scope, ngModel, response.paymentResponse);
                            return;
                        }

                        const request = {
                            merchantTerminalId: scope.merchantTerminal.id
                        };
                        const uri = `/api/paymentAccount/${scope.model.id}`;

                        this.dataService.customMethodData(uri, "DELETE", request)
                            .then(() => {
                                this.updateModel(scope, ngModel, response.paymentResponse);
                            })
                            .finally(() => {
                                this.busyService.stop();
                            });
                    })
                    .catch(response => {
                        if (response && response.hostedPageTerminal) {
                            scope.merchantTerminal = response.hostedPageTerminal;
                            scope.merchantTerminalId = response.hostedPageTerminal.id;
                        }
                    });
            };

            scope.processCreditCard = () => {
                // Hosted-page tokenization never touches the physical terminal, so a paired kiosk can't collide with it.
                if (scope.useHostedPage()) {
                    openAddCreditPaymentAccountModal();
                    return;
                }

                this.tippyService.checkPairedKioskLock(scope.merchantTerminal, scope.drawerId, scope.enableDualLockBehavior).then(() => {
                    openAddCreditPaymentAccountModal();
                });
            };

            scope.isCreditChanged = () => {
                // if method was added before
                if (scope.model.hasOwnProperty('id')) {
                    this.modalService.confirmHandler("MESSAGE_ALERT_PAYMENT_METHOD_CHANGE", Meevo.Components.Modal.ModalTranslationModes.Translate)
                        .then((response: boolean) => {
                            if (response) {
                                ngModel.$validate();
                                scope.model.paymentAccountType = this.togglePaymentType(scope.isCredit);
                            } else {
                                scope.isCredit = !scope.isCredit;   // prevent changing radio status, if user clicks 'No'
                            }
                        })
                } else {
                    ngModel.$validate();
                    scope.model.paymentAccountType = this.togglePaymentType(scope.isCredit);
                }
            };

            scope.deletePaymentAccount = (event: ng.IAngularEvent) => {
                event.stopPropagation();
                if (!scope.disabled && scope.canDelete) {
                    scope.delete();
                }
            }

            scope.useHostedPage = (): boolean => {
                if (!scope.merchantAccount || !scope.merchantTerminal) {
                    return false;
                }

                return scope.merchantAccount.hasHostedPageSupport && scope.merchantTerminal.merchantTerminalType === Meevo.Model.MerchantTerminalTypeEnum.Hosted;
            }

            scope.showAchInfo = (number: string, numberType: string): void => {
                if (_.isFunction(scope.elevationChallenge)) {
                    scope.elevationChallenge({
                        data: {
                            number: number,
                            paymentAccountId: scope.model.id,
                            numberType: numberType
                        }
                    });
                } return;
            }
        };

        static factory(): angular.IDirectiveFactory {
            let directive = (dataService, busyService, modalService, addressFieldsService, tippyService) => {
                return new PaymentAccount(dataService, busyService, modalService, addressFieldsService, tippyService);
            };

            directive.$inject = [
                "meevo.components.common.DataService",
                "meevo.components.busy.BusyService",
                "meevo.components.modal.ModalService",
                "meevo.components.common.AddressFieldsService",
                "meevo.plugins.tippy.tippyService"
            ];

            return directive;
        }
    }

    angular.module("meevo.components.controls")
        .directive("meevoPaymentAccount", PaymentAccount.factory());
}